import { AlertTriangle, ArrowRight, Loader2, Trash2, X } from "lucide-react";
import { Contract } from "../types";

export function DeleteContractModal({
  open,
  contract,
  onClose,
  onConfirm,
  deleting,
  error,
}: {
  open: boolean;
  contract: Contract | null;
  onClose: () => void;
  onConfirm: () => void;
  deleting?: boolean;
  error?: string | null;
}) {
  if (!open || !contract) return null;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/40 px-4 py-6">
      <div className="bg-white rounded-xl shadow-xl w-full max-w-[420px] overflow-hidden flex flex-col">
        <div className="flex items-center justify-between px-4 py-2 border-b border-[#E9E9F0] shrink-0">
          <div>
            <div className="text-[12px] font-bold tracking-[0.12em] text-[#A6A9B6] uppercase">
              Delete Contract
            </div>
            <div className="text-[18px] font-semibold text-[#454450]">
              Are you sure?
            </div>
          </div>
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="text-[#8C8A96] hover:text-[#6F6D7C] bg-white border border-[#ECEEF3] rounded-md cursor-pointer w-8 h-8 flex items-center justify-center disabled:opacity-60 disabled:cursor-not-allowed"
          >
            <X size={14} />
          </button>
        </div>

        <div className="px-5 py-5 flex flex-col gap-4">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-[#FDECEC] flex items-center justify-center shrink-0">
              <AlertTriangle size={20} className="text-[#E54848]" />
            </div>
            <div className="flex flex-col gap-1.5">
              <div className="text-[13px] text-[#525062] leading-relaxed">
                This will permanently delete the contract and all its
                associated data. This action cannot be undone.
              </div>
              <div className="text-[13px] font-semibold text-[#454450] flex items-center flex-wrap gap-1 mt-1">
                <span>{contract.from}</span>
                <ArrowRight className="w-3.5 h-3.5 text-[#E2682B] shrink-0" />
                <span>{contract.to}</span>
              </div>
              {contract.loa && (
                <div className="text-[12px] text-[#828A99]">
                  LoA <span className="text-[#4A5160]">{contract.loa}</span>
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="text-[12px] text-[#E54848] bg-[#FDECEC] rounded-md px-3 py-2">
              {error}
            </div>
          )}
        </div>

        <div className="flex items-center justify-end gap-2 px-5 py-3 border-t border-[#E9E9F0] bg-[#FAFBFD]">
          <button
            type="button"
            onClick={onClose}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 border border-[#ECEEF3] bg-white rounded-lg px-3.5 py-1.5 text-xs text-[#525062] font-medium hover:border-[#DADCE3] hover:bg-[#F4F5F8] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={onConfirm}
            disabled={deleting}
            className="inline-flex items-center gap-1.5 border border-[#E54848] bg-[#E54848] rounded-lg px-3.5 py-1.5 text-xs text-white font-semibold hover:bg-[#D33B3B] hover:border-[#D33B3B] disabled:opacity-60 disabled:cursor-not-allowed cursor-pointer"
          >
            {deleting ? (
              <Loader2 size={14} className="animate-spin" />
            ) : (
              <Trash2 size={14} />
            )}{" "}
            {deleting ? "Deleting…" : "Delete"}
          </button>
        </div>
      </div>
    </div>
  );
}

export default DeleteContractModal;
